/**
 * Online-only field endpoints.
 *
 * Everything here needs a live, server-confirmed request and is NEVER queued:
 * approvals and verification (queuing them would infer consent), re-planning,
 * uploads (presigned URLs a queued mutation could not carry) and reads that
 * only make sense fresh (dashboard, media links, notifications).
 *
 * Offline-capable writes go through the repositories in `@/lib/db` instead.
 */
import { api } from './client';
import {
  dashboardSchema,
  diaryEntrySchema,
  issueSchema,
  mediaAssetSchema,
  milestoneSchema,
  notificationSchema,
  progressUpdateSchema,
  stageSchema,
  uploadInitResponseSchema,
  type AppNotification,
  type Dashboard,
  type DiaryEntry,
  type Issue,
  type MediaAsset,
  type MediaEntityType,
  type MediaKind,
  type Milestone,
  type ProgressUpdate,
  type Stage,
  type UploadInitResponse,
} from './field-types';

const base = (projectId: string) => `/projects/${projectId}`;

// -- dashboard ---------------------------------------------------------------

export async function fetchDashboard(projectId: string): Promise<Dashboard> {
  const response = await api.get<unknown>(`${base(projectId)}/dashboard`);
  return dashboardSchema.parse(response.data);
}

// -- diary -------------------------------------------------------------------

export async function approveDiaryEntry(
  projectId: string,
  entryId: string,
  idempotencyKey: string,
): Promise<DiaryEntry> {
  const response = await api.post<unknown>(
    `${base(projectId)}/diary/${entryId}/approve`,
    {},
    { idempotencyKey },
  );
  return diaryEntrySchema.parse(response.data);
}

// -- progress ----------------------------------------------------------------

export async function reviewProgress(
  projectId: string,
  updateId: string,
  input: { verify: boolean; note: string },
  idempotencyKey: string,
): Promise<ProgressUpdate> {
  const response = await api.post<unknown>(
    `${base(projectId)}/progress/${updateId}/review`,
    input,
    { idempotencyKey },
  );
  return progressUpdateSchema.parse(response.data);
}

// -- issues ------------------------------------------------------------------

export type VerifyTransition = 'close' | 'reopen';

export async function transitionIssueOnline(
  projectId: string,
  issueId: string,
  action: VerifyTransition,
  notes: string,
  idempotencyKey: string,
): Promise<Issue> {
  const response = await api.post<unknown>(
    `${base(projectId)}/issues/${issueId}/transition`,
    { action, notes },
    { idempotencyKey },
  );
  return issueSchema.parse(response.data);
}

// -- planning ----------------------------------------------------------------

export type PlanTemplate = 'residential' | 'commercial' | 'blank';

export async function applyStageTemplate(
  projectId: string,
  template: PlanTemplate,
  idempotencyKey: string,
): Promise<{ stages: Stage[]; milestones: Milestone[] }> {
  const response = await api.post<{ stages: unknown[]; milestones: unknown[] }>(
    `${base(projectId)}/plan/apply-template`,
    { template },
    { idempotencyKey },
  );
  return {
    stages: response.data.stages.map((row) => stageSchema.parse(row)),
    milestones: response.data.milestones.map((row) => milestoneSchema.parse(row)),
  };
}

export type StageInput = Partial<{
  name: string;
  sequence: number;
  status: string;
  planned_start: string | null;
  planned_end: string | null;
  notes: string;
}>;

export async function saveStage(
  projectId: string,
  stageId: string | null,
  data: StageInput,
  baseVersion: number | null,
  idempotencyKey: string,
): Promise<Stage> {
  const response = stageId
    ? await api.patch<unknown>(
        `${base(projectId)}/stages/${stageId}`,
        { data, base_version: baseVersion },
        { idempotencyKey },
      )
    : await api.post<unknown>(`${base(projectId)}/stages`, { data }, { idempotencyKey });
  return stageSchema.parse(response.data);
}

export type MilestoneInput = Partial<{
  name: string;
  stage_id: string | null;
  due_date: string | null;
  status: string;
  requires_owner_approval: boolean;
}>;

export async function saveMilestone(
  projectId: string,
  milestoneId: string | null,
  data: MilestoneInput,
  idempotencyKey: string,
): Promise<Milestone> {
  const response = milestoneId
    ? await api.patch<unknown>(
        `${base(projectId)}/milestones/${milestoneId}`,
        { data },
        { idempotencyKey },
      )
    : await api.post<unknown>(`${base(projectId)}/milestones`, { data }, { idempotencyKey });
  return milestoneSchema.parse(response.data);
}

// -- media -------------------------------------------------------------------

export type UploadInitInput = {
  asset_id: string;
  kind: MediaKind;
  content_type: string;
  size_bytes: number;
  sha256: string;
  /** Register files (`document_revision`) upload online, outside the media queue. */
  entity_type: MediaEntityType | 'document_revision';
  entity_id: string;
  original_filename?: string;
  caption?: string;
  captured_at_device?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  gps_accuracy_m?: number | null;
};

export async function initUpload(
  projectId: string,
  input: UploadInitInput,
): Promise<UploadInitResponse> {
  // Keyed on the device's asset id: a retry is the same upload, not a second photo.
  const response = await api.post<unknown>(`${base(projectId)}/uploads/init`, input, {
    idempotencyKey: `upload-init:${input.asset_id}`,
  });
  return uploadInitResponseSchema.parse(response.data);
}

export async function completeUpload(projectId: string, assetId: string): Promise<MediaAsset> {
  const response = await api.post<unknown>(
    `${base(projectId)}/uploads/${assetId}/complete`,
    {},
    { idempotencyKey: `upload-complete:${assetId}` },
  );
  return mediaAssetSchema.parse(response.data);
}

export type MediaVariant = 'thumb' | 'display' | 'original';

/** Short-lived presigned link (minutes). Never persist it. */
export async function fetchMediaUrl(
  projectId: string,
  assetId: string,
  variant: MediaVariant = 'display',
): Promise<string> {
  const response = await api.get<{ url: string }>(`${base(projectId)}/media/${assetId}/url`, {
    query: { variant },
  });
  return response.data.url;
}

// -- invitations -------------------------------------------------------------

export type InvitationInput = {
  role: string;
  party_role: string;
  email?: string;
  phone?: string;
  party_id?: string;
};

export async function createInvitation(
  projectId: string,
  input: InvitationInput,
  idempotencyKey: string,
): Promise<{ id: string; token: string; expires_at: string }> {
  const response = await api.post<{ id: string; token: string; expires_at: string }>(
    `${base(projectId)}/invitations`,
    input,
    { idempotencyKey },
  );
  return response.data;
}

export async function acceptInvitation(
  token: string,
  idempotencyKey: string,
  organizationId?: string,
): Promise<{ project_id: string; role: string }> {
  const response = await api.post<{ project_id: string; role: string }>(
    '/projects/invitations/accept',
    organizationId ? { token, organization_id: organizationId } : { token },
    { idempotencyKey },
  );
  return response.data;
}

// -- notifications -----------------------------------------------------------

export async function fetchNotifications(
  options: { unreadOnly?: boolean; limit?: number } = {},
): Promise<{ items: AppNotification[]; unread: number }> {
  const response = await api.get<unknown[]>('/notifications', {
    query: { unread_only: options.unreadOnly ?? false, limit: options.limit ?? 50 },
  });
  const items = (response.data ?? []).flatMap((row) => {
    const parsed = notificationSchema.safeParse(row);
    return parsed.success ? [parsed.data] : [];
  });
  const unread = typeof response.meta['unread'] === 'number' ? response.meta['unread'] : 0;
  return { items, unread };
}

export async function markNotificationRead(id: string): Promise<void> {
  await api.post(`/notifications/${id}/read`, {}, { idempotencyKey: `notification-read:${id}` });
}

export async function markAllNotificationsRead(group?: string): Promise<void> {
  await api.post('/notifications/read-all', {}, { query: { group } });
}
